/**
 * Contact detail is rows, not one phone column and one email column (piece 13,
 * decision 0170). 🔨 `extension?` is its own field: not folded into `value`,
 * because a dialer needs them apart, and not into `label?`, which names the line
 * rather than giving dialing instructions.
 */
import { isLinkOpen } from '../linkRow';
import { isoDay } from '../zonedTime';
import { emailDomain } from '../records/normalise';
import { isWebContactValue, telHref, webHref } from '../records/recordPicture';
import type { StringKey } from '../strings';

export const CONTACT_METHOD_KINDS = ['WorkEmail', 'PersonalEmail', 'Desk', 'Mobile', 'Fax', 'Other'] as const;
export type ContactMethodKind = (typeof CONTACT_METHOD_KINDS)[number];

/** The kinds that carry an address rather than a number, and so are validated as one. */
export const EMAIL_KINDS: readonly ContactMethodKind[] = ['WorkEmail', 'PersonalEmail'];

export class ContactMethodError extends Error {}

export interface ContactMethodRow {
  id?: string;
  employmentId: string;
  kind: ContactMethodKind;
  value: string;
  extension?: string | null;
  label?: string | null;
  isPrimary?: boolean;
  startDate: Date;
  endDate?: Date | null;
}

/**
 * 🔨 A row holds the primary place, or its address, only while it runs past `day`,
 * the organization's day: one ending on that day gives the place up the same day,
 * so a correction can take it at once (piece 10, a correction in place; decision
 * 2860 as amended in review). A reader still finds the row through that day.
 */
function holdsPlace(row: ContactMethodRow, day: string): boolean {
  return row.endDate === null || row.endDate === undefined || isoDay(row.endDate) > isoDay(day);
}

/** 🔨 At most one active isPrimary per (employmentId, kind); a consumer asks for the kind it needs. */
export function assertOnePrimary(candidate: ContactMethodRow, existing: readonly ContactMethodRow[], day: string): void {
  if (!candidate.isPrimary) return;
  const clash = existing.find(
    (row) => row.id !== candidate.id && row.employmentId === candidate.employmentId && row.kind === candidate.kind && row.isPrimary && holdsPlace(row, day),
  );
  if (clash) throw new ContactMethodError(`one active primary ${candidate.kind} per employment; another row already holds it`);
}

/** Only a desk line has an extension (product owner, 2026-09-12): the one rule both apps read. */
export function takesExtension(kind: ContactMethodKind): boolean {
  return kind === 'Desk';
}

/** An extension belongs to a line, never to an address. */
export function assertExtensionShape(row: ContactMethodRow): void {
  if (row.extension && EMAIL_KINDS.includes(row.kind)) throw new ContactMethodError('an email address carries no extension');
}

/**
 * The (companyId, emailAddress) uniqueness rule moves with the split: an active
 * WorkEmail value is unique within a company, reached through employmentId to
 * companyId, because the row does not know its own company.
 */
export function assertWorkEmailUniqueInCompany(
  candidate: ContactMethodRow,
  existing: readonly (ContactMethodRow & { companyId: string })[],
  candidateCompanyId: string,
  day: string,
): void {
  if (candidate.kind !== 'WorkEmail') return;
  const value = candidate.value.trim().toLowerCase();
  const clash = existing.find(
    (row) => row.id !== candidate.id && row.kind === 'WorkEmail' && row.companyId === candidateCompanyId && row.value.trim().toLowerCase() === value && holdsPlace(row, day),
  );
  if (clash) throw new ContactMethodError('that work email is already in use at this company');
}

export function contactMethodProblems(row: ContactMethodRow): string[] {
  const problems: string[] = [];
  if (!row.value.trim()) problems.push('a contact method carries a value');
  if (EMAIL_KINDS.includes(row.kind) && !emailDomain(row.value)) problems.push('an email address needs a domain');
  if (row.endDate && row.endDate.getTime() < row.startDate.getTime()) problems.push('a contact method cannot end before it starts');
  return problems;
}

/** What a consumer asks for: the active primary of one kind, or the first active row of it. */
export function preferredMethod(rows: readonly ContactMethodRow[], employmentId: string, kind: ContactMethodKind, day: string): ContactMethodRow | null {
  const active = rows.filter((row) => row.employmentId === employmentId && row.kind === kind && isLinkOpen(row, day));
  return active.find((row) => row.isPrimary) ?? active[0] ?? null;
}

/** The address the domain interlock matches: the employment's own WorkEmail row (decision 0157). */
export function workEmailFor(rows: readonly ContactMethodRow[], employmentId: string, day: string): string | null {
  return preferredMethod(rows, employmentId, 'WorkEmail', day)?.value ?? null;
}


/**
 * 🔒 The stacked contact row is the standard every phone and email item draws
 * through (decision 0623). The kind's own name in human English leads the row,
 * bold, and this is the one map from the kind to that name. *"Name in Human
 * English formatted bold"* — product owner, 2026-09-22.
 */
export const CONTACT_METHOD_KIND_LABEL_KEYS: Readonly<Record<ContactMethodKind, StringKey>> = {
  WorkEmail: 'contact.kind.workEmail',
  PersonalEmail: 'contact.kind.personalEmail',
  Desk: 'contact.kind.desk',
  Mobile: 'contact.kind.mobile',
  Fax: 'contact.kind.fax',
  Other: 'contact.kind.other',
};

/**
 * What the row's right-hand column offers: a call on a line somebody dials, a
 * message on an address, an opened tab on a typed web address, and nothing at
 * all on a fax or a note. 🔨 A fax is not dialled from a browser and a note
 * names nowhere to go.
 */
export type ContactRowAction = 'call' | 'email' | 'web' | 'none';

export interface ContactRowReach {
  action: ContactRowAction;
  /** Where the action goes; empty where there is none. */
  href: string;
}

/**
 * The one reach a contact line has, derived from the line itself rather than
 * from any registry. 🔨 The dialer link carries the extension through `telHref`,
 * so a desk line behind a switchboard dials whole where the handset understands
 * it and dials the number where it does not (decision 0623).
 */
export function contactRowReach(kind: ContactMethodKind, value: string, extension?: string | null): ContactRowReach {
  const trimmed = value.trim();
  if (trimmed === '') return { action: 'none', href: '' };
  if (EMAIL_KINDS.includes(kind)) return { action: 'email', href: `mailto:${trimmed}` };
  if (kind === 'Desk' || kind === 'Mobile') return { action: 'call', href: telHref(trimmed, extension ?? null) };
  if (kind === 'Other' && isWebContactValue(trimmed)) return { action: 'web', href: webHref(trimmed) };
  return { action: 'none', href: '' };
}

/**
 * 🔒 One line a Person row can reach, as the server carries it on the row (T214;
 * decision 0692). The product owner, 2026-09-24: *"is there a chevron for
 * multiple email or call number"*. A person with a desk line and a mobile was
 * offered one Call and no way to the other.
 *
 * `display` is the line as every page draws it, formatted by the server through
 * the one phone formatter (decision 0340); `value` and `extension` are what the
 * dialer link is built from.
 */
export interface ReachLine {
  kind: ContactMethodKind;
  value: string;
  extension: string | null;
  label: string | null;
  isPrimary: boolean;
  display: string;
}

/** The kinds a Person row reaches: the two a person dials, then the two a message goes to. A fax and a note are not reached from a row. */
export const REACH_LINE_KINDS: readonly ContactMethodKind[] = ['Desk', 'Mobile', 'WorkEmail', 'PersonalEmail'];

/**
 * 🔒 The order a row's lines are offered in (decision 0692): the primary line
 * first, then a desk line before a mobile and a work address before a personal
 * one, then the order they arrived in, which the server reads oldest first. The
 * first line of each reach is what its button takes without the chevron.
 */
export function orderReachLines<L extends Pick<ReachLine, 'kind' | 'isPrimary'>>(lines: readonly L[]): L[] {
  return lines
    .map((line, index) => ({ line, index }))
    .filter(({ line }) => REACH_LINE_KINDS.includes(line.kind))
    .sort((a, b) => Number(b.line.isPrimary) - Number(a.line.isPrimary) || REACH_LINE_KINDS.indexOf(a.line.kind) - REACH_LINE_KINDS.indexOf(b.line.kind) || a.index - b.index)
    .map(({ line }) => line);
}

/**
 * The row's lines read back off the JSON the server wrote, in the server's
 * order. 🔨 A value that is not the list, or an entry missing its kind or its
 * value, is dropped rather than drawn: a row reads its one line, or none, and
 * never a Call to nowhere.
 */
export function reachLinesFrom(json: unknown): ReachLine[] {
  if (typeof json !== 'string' || json === '') return [];
  let parsed: unknown;
  try {
    parsed = JSON.parse(json);
  } catch {
    return [];
  }
  if (!Array.isArray(parsed)) return [];
  return parsed.flatMap((entry: unknown): ReachLine[] => {
    if (typeof entry !== 'object' || entry === null) return [];
    const row = entry as Record<string, unknown>;
    const kind = typeof row.kind === 'string' && (REACH_LINE_KINDS as readonly string[]).includes(row.kind) ? (row.kind as ContactMethodKind) : null;
    const value = typeof row.value === 'string' ? row.value.trim() : '';
    if (kind === null || value === '') return [];
    return [{
      kind,
      value,
      extension: typeof row.extension === 'string' && row.extension !== '' ? row.extension : null,
      label: typeof row.label === 'string' && row.label !== '' ? row.label : null,
      isPrimary: row.isPrimary === true,
      display: typeof row.display === 'string' && row.display !== '' ? row.display : value,
    }];
  });
}
